/**
 * Partnership GTM Dashboard
 * Admin page for managing partnership pipeline, outreach, and metrics.
 * Uses AdminLayout (logo + "Admin Console" header) to match SEAL UI.
 */

import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
// Admin-plane API — sends adminAccessToken + x-2fa-session. The underlying
// /api/wiki/partnerships/* proxy routes are authenticateAdmin-gated.
import adminApi from '../../services/adminApi';

const STAGE_CONFIG = {
  discovered:    { icon: '🔍', color: '#94a3b8', label: 'Research' },
  outreach_sent: { icon: '📧', color: '#3b82f6', label: 'Outreach Sent' },
  in_discussion: { icon: '💬', color: '#f59e0b', label: 'In Discussion' },
  agreed:        { icon: '✅', color: '#10b981', label: 'Agreed' },
  active:        { icon: '🤝', color: '#16a34a', label: 'Partner' },
  strategic:     { icon: '⭐', color: '#7c3aed', label: 'Strategic Partner' },
  declined:      { icon: '❌', color: '#dc2626', label: 'Declined' }
};

const STAGE_ORDER = ['discovered', 'outreach_sent', 'in_discussion', 'agreed', 'active', 'strategic', 'declined'];

const PartnershipDashboard = () => {
  const [partnerships, setPartnerships] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [activeTab, setActiveTab] = useState('pipeline');
  const [filterStage, setFilterStage] = useState('all');
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showOutreach, setShowOutreach] = useState(false);
  const [outreachBody, setOutreachBody] = useState('');
  const [outreachSubject, setOutreachSubject] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  const fetchPartnerships = async () => {
    try {
      const res = await adminApi.get(
        `/api/wiki/partnerships${filterStage !== 'all' ? `?stage=${filterStage}` : ''}`
      );
      setPartnerships(res.data?.partnerships || []);
    } catch (e) {
      console.error('Partnerships fetch error:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchMetrics = async () => {
    try {
      const res = await adminApi.get('/api/wiki/partnerships/metrics');
      setMetrics(res.data?.metrics || null);
    } catch (e) {
      console.error('Metrics fetch error:', e);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await adminApi.get('/api/wiki/partnerships/templates');
      setTemplates(res.data?.templates || []);
    } catch (e) {
      console.error('Templates fetch error:', e);
    }
  };

  useEffect(() => {
    fetchPartnerships();
    fetchMetrics();
    fetchTemplates();
  }, [filterStage]);

  const handleStageChange = async (id, stage) => {
    setActionLoading(prev => ({ ...prev, [id]: stage }));
    try {
      await adminApi.patch(`/api/wiki/partnerships/${id}/stage`, { stage });
      fetchPartnerships();
      fetchMetrics();
    } catch (e) {
      console.error('Stage change error:', e);
    } finally {
      setActionLoading(prev => {
        const n = { ...prev };
        delete n[id];
        return n;
      });
    }
  };

  const handleSendOutreach = async (partner) => {
    setActionLoading(prev => ({ ...prev, [partner.id]: 'outreach' }));
    try {
      await adminApi.post(`/api/wiki/partnerships/${partner.id}/communicate`, {
        direction: 'outbound',
        channel: 'email',
        subject: outreachSubject,
        body: outreachBody,
        sent_by: 'admin'
      });
      await handleStageChange(partner.id, 'outreach_sent');
      setShowOutreach(false);
      setSelectedPartner(null);
      setOutreachBody('');
      setOutreachSubject('');
    } catch (e) {
      console.error('Outreach error:', e);
    } finally {
      setActionLoading(prev => {
        const n = { ...prev };
        delete n[partner.id];
        return n;
      });
    }
  };

  const applyTemplate = (template, partner) => {
    const filled = template.body
      .replace(/\{\{name\}\}/g, partner.name)
      .replace(/\{\{channel\}\}/g, partner.youtube_channel_url || partner.name)
      .replace(/\{\{metrics\}\}/g,
        partner.scout_metrics?.followers
          ? `${partner.scout_metrics.followers} subscribers`
          : 'growing audience'
      );
    setOutreachSubject(template.subject.replace(/\{\{name\}\}/g, partner.name));
    setOutreachBody(filled);
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('en-IN', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const needsAction = partnerships.filter(p => p.stage === 'discovered').length;

  if (loading) {
    return (
      <AdminLayout showBackButton backPath="/admin/dashboard">
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: 20 }}>
          <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
            Loading Partnership Pipeline...
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout showBackButton backPath="/admin/dashboard">
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: 20 }}>

        {/* Top Bar */}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 style={{
              margin: 0, fontSize: 22, fontWeight: 700, color: '#1e293b',
              display: 'flex', alignItems: 'center', gap: 8
            }}>
              <span style={{ fontSize: 24 }}>🤝</span>
              Partnership Pipeline
            </h1>
            {needsAction > 0 && (
              <span style={{
                background: '#fefce8', color: '#ca8a04',
                padding: '4px 12px', borderRadius: 20,
                fontSize: 13, fontWeight: 600
              }}>
                {needsAction} awaiting outreach
              </span>
            )}
          </div>
          <button
            onClick={() => { setLoading(true); fetchPartnerships(); fetchMetrics(); }}
            style={{
              padding: '8px 16px', background: '#fff',
              border: '1px solid #e5e7eb', borderRadius: 8,
              cursor: 'pointer', fontSize: 13, color: '#64748b'
            }}
          >
            ⟳ Refresh
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex', gap: 4, marginBottom: 24,
          borderBottom: '2px solid #e5e7eb'
        }}>
          {[
            { key: 'pipeline', label: 'Pipeline', icon: '📋', badge: partnerships.length },
            { key: 'outreach', label: 'Outreach', icon: '📧' },
            { key: 'metrics', label: 'Metrics', icon: '📊' }
          ].map(tab => (
            <button key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '10px 20px', border: 'none', background: 'none',
                cursor: 'pointer', fontSize: 14,
                fontWeight: activeTab === tab.key ? 600 : 400,
                color: activeTab === tab.key ? '#7c3aed' : '#6b7280',
                borderBottom: activeTab === tab.key
                  ? '2px solid #7c3aed' : '2px solid transparent',
                marginBottom: -2,
                display: 'flex', alignItems: 'center', gap: 6
              }}
            >
              {tab.icon} {tab.label}
              {tab.badge > 0 && (
                <span style={{
                  background: '#7c3aed', color: '#fff',
                  borderRadius: 12, padding: '2px 8px', fontSize: 11
                }}>
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ═══ PIPELINE TAB ═══ */}
        {activeTab === 'pipeline' && (
          <div>
            {/* Stage filter buttons */}
            <div style={{
              display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20
            }}>
              <button
                onClick={() => setFilterStage('all')}
                style={{
                  padding: '6px 14px', borderRadius: 8, fontSize: 13,
                  border: filterStage === 'all' ? '2px solid #7c3aed' : '1px solid #e5e7eb',
                  background: filterStage === 'all' ? '#f5f3ff' : '#fff',
                  color: filterStage === 'all' ? '#7c3aed' : '#6b7280',
                  cursor: 'pointer', fontWeight: filterStage === 'all' ? 600 : 400
                }}
              >
                All
              </button>
              {STAGE_ORDER.map(stage => {
                const cfg = STAGE_CONFIG[stage];
                return (
                  <button key={stage}
                    onClick={() => setFilterStage(stage)}
                    style={{
                      padding: '6px 14px', borderRadius: 8, fontSize: 13,
                      border: filterStage === stage ? `2px solid ${cfg.color}` : '1px solid #e5e7eb',
                      background: filterStage === stage ? cfg.color + '12' : '#fff',
                      color: filterStage === stage ? cfg.color : '#6b7280',
                      cursor: 'pointer', fontWeight: filterStage === stage ? 600 : 400
                    }}
                  >
                    {cfg.icon} {cfg.label}
                  </button>
                );
              })}
            </div>

            {/* Partnership cards */}
            {partnerships.length === 0 ? (
              <div style={{
                background: '#fff', borderRadius: 12, padding: 60,
                textAlign: 'center', border: '1px solid #e5e7eb'
              }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
                <h3 style={{ color: '#1e293b', marginBottom: 8 }}>
                  No partnerships found
                </h3>
                <p style={{ color: '#94a3b8', margin: 0 }}>
                  {filterStage !== 'all'
                    ? `No partnerships in "${STAGE_CONFIG[filterStage]?.label}" stage`
                    : 'Scout agent will discover potential partners automatically'}
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {partnerships.map(partner => {
                  const cfg = STAGE_CONFIG[partner.stage] || STAGE_CONFIG.discovered;
                  return (
                    <div key={partner.id}>
                      <div style={{
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        borderLeft: `4px solid ${cfg.color}`,
                        borderRadius: 10,
                        padding: 16
                      }}>
                        {/* Card header */}
                        <div style={{
                          display: 'flex', justifyContent: 'space-between',
                          alignItems: 'center', marginBottom: 8,
                          flexWrap: 'wrap', gap: 8
                        }}>
                          <div style={{
                            display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap'
                          }}>
                            <span style={{
                              fontSize: 12, padding: '2px 10px', borderRadius: 10,
                              background: cfg.color + '15', color: cfg.color, fontWeight: 600
                            }}>
                              {cfg.icon} {cfg.label}
                            </span>
                            {partner.domain && (
                              <span style={{
                                fontSize: 12, padding: '2px 10px', borderRadius: 10,
                                background: '#f3f4f6', color: '#6b7280'
                              }}>
                                {partner.domain.toUpperCase()}
                              </span>
                            )}
                            {partner.partner_type && (
                              <span style={{
                                fontSize: 12, padding: '2px 10px', borderRadius: 10,
                                background: '#eff6ff', color: '#3b82f6'
                              }}>
                                {partner.partner_type}
                              </span>
                            )}
                          </div>
                          <span style={{ fontSize: 13, color: '#94a3b8' }}>
                            {formatTime(partner.discovered_at || partner.created_at)}
                          </span>
                        </div>

                        {/* Name */}
                        <h3 style={{
                          margin: '0 0 8px', fontSize: 15,
                          fontWeight: 600, color: '#1e293b'
                        }}>
                          {partner.name}
                        </h3>

                        {/* Description / notes */}
                        {partner.notes && (
                          <p style={{
                            color: '#475569', fontSize: 14, lineHeight: 1.6,
                            margin: '0 0 12px', background: '#f9fafb',
                            padding: 12, borderRadius: 8
                          }}>
                            {partner.notes.substring(0, 300)}
                            {partner.notes.length > 300 ? '...' : ''}
                          </p>
                        )}

                        {/* Meta footer */}
                        <div style={{
                          display: 'flex', gap: 16, marginBottom: 12,
                          fontSize: 13, color: '#6b7280', flexWrap: 'wrap'
                        }}>
                          {partner.scout_metrics?.followers && (
                            <span>
                              Followers: <strong>{partner.scout_metrics.followers.toLocaleString()}</strong>
                            </span>
                          )}
                          {partner.youtube_channel_url && (
                            <a
                              href={partner.youtube_channel_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#3b82f6', textDecoration: 'none' }}
                            >
                              🔗 Channel
                            </a>
                          )}
                          {partner.discovery_source && (
                            <span>
                              Source: <strong>{partner.discovery_source}</strong>
                            </span>
                          )}
                        </div>

                        {/* Action buttons based on stage */}
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {partner.stage === 'discovered' && (
                            <button
                              onClick={() => {
                                setSelectedPartner(partner);
                                setShowOutreach(true);
                                setOutreachBody('');
                                setOutreachSubject('');
                              }}
                              style={{
                                padding: '8px 16px', background: '#3b82f6',
                                color: '#fff', border: 'none', borderRadius: 8,
                                cursor: 'pointer', fontSize: 13, fontWeight: 500
                              }}
                            >
                              📧 Send Outreach
                            </button>
                          )}
                          {partner.stage === 'outreach_sent' && (
                            <>
                              <button
                                onClick={() => handleStageChange(partner.id, 'in_discussion')}
                                disabled={!!actionLoading[partner.id]}
                                style={{
                                  padding: '8px 16px', background: '#f59e0b',
                                  color: '#fff', border: 'none', borderRadius: 8,
                                  cursor: 'pointer', fontSize: 13, fontWeight: 500,
                                  opacity: actionLoading[partner.id] ? 0.7 : 1
                                }}
                              >
                                💬 Mark: In Discussion
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedPartner(partner);
                                  setShowOutreach(true);
                                  setOutreachBody('');
                                  setOutreachSubject('');
                                }}
                                style={{
                                  padding: '8px 16px', background: '#fff',
                                  color: '#3b82f6', border: '1px solid #3b82f6',
                                  borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500
                                }}
                              >
                                🔄 Send Follow-up
                              </button>
                            </>
                          )}
                          {partner.stage === 'in_discussion' && (
                            <>
                              <button
                                onClick={() => handleStageChange(partner.id, 'agreed')}
                                disabled={!!actionLoading[partner.id]}
                                style={{
                                  padding: '8px 16px', background: '#10b981',
                                  color: '#fff', border: 'none', borderRadius: 8,
                                  cursor: 'pointer', fontSize: 13, fontWeight: 500,
                                  opacity: actionLoading[partner.id] ? 0.7 : 1
                                }}
                              >
                                ✅ Mark: Agreed
                              </button>
                              <button
                                onClick={() => handleStageChange(partner.id, 'declined')}
                                disabled={!!actionLoading[partner.id]}
                                style={{
                                  padding: '8px 16px', background: '#fff',
                                  color: '#dc2626', border: '1px solid #dc2626',
                                  borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500
                                }}
                              >
                                ❌ Declined
                              </button>
                            </>
                          )}
                          {partner.stage === 'agreed' && (
                            <button
                              onClick={() => handleStageChange(partner.id, 'active')}
                              disabled={!!actionLoading[partner.id]}
                              style={{
                                padding: '8px 16px', background: '#16a34a',
                                color: '#fff', border: 'none', borderRadius: 8,
                                cursor: 'pointer', fontSize: 13, fontWeight: 500,
                                opacity: actionLoading[partner.id] ? 0.7 : 1
                              }}
                            >
                              🤝 Activate Partnership
                            </button>
                          )}
                          {partner.stage === 'active' && (
                            <button
                              onClick={() => handleStageChange(partner.id, 'strategic')}
                              disabled={!!actionLoading[partner.id]}
                              style={{
                                padding: '8px 16px', background: '#7c3aed',
                                color: '#fff', border: 'none', borderRadius: 8,
                                cursor: 'pointer', fontSize: 13, fontWeight: 500,
                                opacity: actionLoading[partner.id] ? 0.7 : 1
                              }}
                            >
                              ⭐ Upgrade: Strategic
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Inline outreach form */}
                      {showOutreach && selectedPartner?.id === partner.id && (
                        <div style={{
                          background: '#fff', border: '1px solid #3b82f6',
                          borderRadius: '0 0 10px 10px', borderTop: 'none',
                          padding: 20, marginTop: -4
                        }}>
                          <h4 style={{ margin: '0 0 12px', fontSize: 15, color: '#1e293b' }}>
                            📧 Compose Outreach to {partner.name}
                          </h4>

                          {/* Template selector */}
                          {templates.length > 0 && (
                            <div style={{ marginBottom: 12 }}>
                              <label style={{ fontSize: 13, color: '#6b7280', display: 'block', marginBottom: 4 }}>
                                Template:
                              </label>
                              <select
                                onChange={(e) => {
                                  const tmpl = templates.find(t => t.id === e.target.value);
                                  if (tmpl) applyTemplate(tmpl, partner);
                                }}
                                style={{
                                  width: '100%', padding: '8px 12px', borderRadius: 8,
                                  border: '1px solid #e5e7eb', fontSize: 14
                                }}
                                defaultValue=""
                              >
                                <option value="" disabled>Select a template...</option>
                                {templates.map(t => (
                                  <option key={t.id} value={t.id}>{t.name}</option>
                                ))}
                              </select>
                            </div>
                          )}

                          {/* Subject */}
                          <div style={{ marginBottom: 12 }}>
                            <label style={{ fontSize: 13, color: '#6b7280', display: 'block', marginBottom: 4 }}>
                              Subject:
                            </label>
                            <input
                              type="text"
                              value={outreachSubject}
                              onChange={(e) => setOutreachSubject(e.target.value)}
                              placeholder="Email subject..."
                              style={{
                                width: '100%', padding: '8px 12px', borderRadius: 8,
                                border: '1px solid #e5e7eb', fontSize: 14, boxSizing: 'border-box'
                              }}
                            />
                          </div>

                          {/* Body */}
                          <div style={{ marginBottom: 16 }}>
                            <label style={{ fontSize: 13, color: '#6b7280', display: 'block', marginBottom: 4 }}>
                              Body:
                            </label>
                            <textarea
                              value={outreachBody}
                              onChange={(e) => setOutreachBody(e.target.value)}
                              placeholder="Email body..."
                              rows={8}
                              style={{
                                width: '100%', padding: '8px 12px', borderRadius: 8,
                                border: '1px solid #e5e7eb', fontSize: 14,
                                resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box'
                              }}
                            />
                          </div>

                          {/* Actions */}
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  `Subject: ${outreachSubject}\n\n${outreachBody}`
                                );
                              }}
                              style={{
                                padding: '8px 16px', background: '#fff',
                                color: '#6b7280', border: '1px solid #e5e7eb',
                                borderRadius: 8, cursor: 'pointer', fontSize: 13
                              }}
                            >
                              📋 Copy to Clipboard
                            </button>
                            <button
                              onClick={() => handleSendOutreach(partner)}
                              disabled={!!actionLoading[partner.id] || !outreachSubject.trim()}
                              style={{
                                padding: '8px 16px', background: '#3b82f6',
                                color: '#fff', border: 'none', borderRadius: 8,
                                cursor: 'pointer', fontSize: 13, fontWeight: 500,
                                opacity: actionLoading[partner.id] ? 0.7 : 1
                              }}
                            >
                              {actionLoading[partner.id] === 'outreach' ? 'Sending...' : '✅ Mark as Sent'}
                            </button>
                            <button
                              onClick={() => { setShowOutreach(false); setSelectedPartner(null); }}
                              style={{
                                padding: '8px 16px', background: '#fff',
                                color: '#dc2626', border: '1px solid #dc2626',
                                borderRadius: 8, cursor: 'pointer', fontSize: 13
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ═══ OUTREACH TAB ═══ */}
        {activeTab === 'outreach' && (
          <div>
            {(() => {
              const outreachPartners = partnerships.filter(
                p => p.stage === 'outreach_sent' || p.stage === 'in_discussion'
              );
              if (outreachPartners.length === 0) {
                return (
                  <div style={{
                    background: '#fff', borderRadius: 12, padding: 60,
                    textAlign: 'center', border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>📧</div>
                    <h3 style={{ color: '#1e293b', marginBottom: 8 }}>
                      No active outreach
                    </h3>
                    <p style={{ color: '#94a3b8', margin: 0 }}>
                      Send outreach from the Pipeline tab to see activity here
                    </p>
                  </div>
                );
              }
              return (
                <div style={{
                  background: '#fff', border: '1px solid #e5e7eb',
                  borderRadius: 12, padding: 20
                }}>
                  <h3 style={{ margin: '0 0 16px', fontSize: 16, color: '#1e293b' }}>
                    Outreach Timeline
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    {outreachPartners.map(partner => {
                      const cfg = STAGE_CONFIG[partner.stage];
                      return (
                        <div key={partner.id} style={{
                          display: 'flex', alignItems: 'flex-start', gap: 12,
                          padding: '14px 0',
                          borderBottom: '1px solid #f1f5f9'
                        }}>
                          <div style={{
                            width: 36, height: 36, borderRadius: '50%',
                            background: cfg.color + '15', color: cfg.color,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 16, flexShrink: 0
                          }}>
                            {cfg.icon}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>
                              {partner.name}
                            </div>
                            <div style={{
                              fontSize: 13, color: '#6b7280', marginTop: 2,
                              display: 'flex', gap: 12, flexWrap: 'wrap'
                            }}>
                              <span style={{
                                color: cfg.color, fontWeight: 500
                              }}>
                                {cfg.label}
                              </span>
                              {partner.domain && (
                                <span>{partner.domain.toUpperCase()}</span>
                              )}
                              <span>{formatTime(partner.updated_at || partner.created_at)}</span>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                            {partner.stage === 'outreach_sent' && (
                              <button
                                onClick={() => handleStageChange(partner.id, 'in_discussion')}
                                disabled={!!actionLoading[partner.id]}
                                style={{
                                  padding: '6px 12px', background: '#f59e0b',
                                  color: '#fff', border: 'none', borderRadius: 6,
                                  cursor: 'pointer', fontSize: 12, fontWeight: 500
                                }}
                              >
                                💬 Got Response
                              </button>
                            )}
                            {partner.stage === 'in_discussion' && (
                              <button
                                onClick={() => handleStageChange(partner.id, 'agreed')}
                                disabled={!!actionLoading[partner.id]}
                                style={{
                                  padding: '6px 12px', background: '#10b981',
                                  color: '#fff', border: 'none', borderRadius: 6,
                                  cursor: 'pointer', fontSize: 12, fontWeight: 500
                                }}
                              >
                                ✅ Agreed
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* ═══ METRICS TAB ═══ */}
        {activeTab === 'metrics' && (
          <div>
            {!metrics ? (
              <div style={{
                background: '#fff', borderRadius: 12, padding: 60,
                textAlign: 'center', border: '1px solid #e5e7eb'
              }}>
                <p style={{ color: '#94a3b8' }}>No metrics available yet</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {/* Funnel visualization */}
                <div style={{
                  background: '#fff', border: '1px solid #e5e7eb',
                  borderRadius: 12, padding: 20
                }}>
                  <h3 style={{ margin: '0 0 16px', fontSize: 16, color: '#1e293b' }}>
                    Partnership Funnel
                  </h3>
                  {(() => {
                    const stageCounts = metrics.by_stage || {};
                    const maxCount = Math.max(...Object.values(stageCounts), 1);
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {STAGE_ORDER.filter(s => s !== 'declined').map(stage => {
                          const cfg = STAGE_CONFIG[stage];
                          const count = stageCounts[stage] || 0;
                          const pct = Math.round((count / maxCount) * 100);
                          return (
                            <div key={stage} style={{
                              display: 'flex', alignItems: 'center', gap: 12
                            }}>
                              <div style={{
                                width: 130, fontSize: 13, color: '#6b7280',
                                display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0
                              }}>
                                {cfg.icon} {cfg.label}
                              </div>
                              <div style={{
                                fontSize: 14, fontWeight: 700, color: cfg.color,
                                width: 30, textAlign: 'right', flexShrink: 0
                              }}>
                                {count}
                              </div>
                              <div style={{
                                flex: 1, height: 24, background: '#f3f4f6',
                                borderRadius: 6, overflow: 'hidden'
                              }}>
                                <div style={{
                                  width: `${pct}%`, height: '100%',
                                  background: cfg.color, borderRadius: 6,
                                  minWidth: count > 0 ? 8 : 0,
                                  transition: 'width 0.3s'
                                }} />
                              </div>
                            </div>
                          );
                        })}
                        {(stageCounts.declined || 0) > 0 && (
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: 12,
                            marginTop: 8, paddingTop: 8, borderTop: '1px solid #f1f5f9'
                          }}>
                            <div style={{
                              width: 130, fontSize: 13, color: '#6b7280',
                              display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0
                            }}>
                              ❌ Declined
                            </div>
                            <div style={{
                              fontSize: 14, fontWeight: 700, color: '#dc2626',
                              width: 30, textAlign: 'right', flexShrink: 0
                            }}>
                              {stageCounts.declined}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Conversion rate */}
                  {metrics.total > 0 && (
                    <div style={{
                      marginTop: 16, paddingTop: 16, borderTop: '1px solid #f1f5f9',
                      display: 'flex', gap: 24, fontSize: 14, color: '#6b7280'
                    }}>
                      <span>
                        Total: <strong style={{ color: '#1e293b' }}>{metrics.total}</strong>
                      </span>
                      <span>
                        Conversion: <strong style={{ color: '#16a34a' }}>
                          {Math.round(
                            ((metrics.by_stage?.active || 0) + (metrics.by_stage?.strategic || 0))
                            / metrics.total * 100
                          )}%
                        </strong>
                      </span>
                    </div>
                  )}
                </div>

                {/* By Domain */}
                {metrics.by_domain && Object.keys(metrics.by_domain).length > 0 && (
                  <div style={{
                    background: '#fff', border: '1px solid #e5e7eb',
                    borderRadius: 12, padding: 20
                  }}>
                    <h3 style={{ margin: '0 0 16px', fontSize: 16, color: '#1e293b' }}>
                      By Domain
                    </h3>
                    {Object.entries(metrics.by_domain).map(([domain, count]) => (
                      <div key={domain} style={{
                        display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', padding: '10px 0',
                        borderBottom: '1px solid #f1f5f9'
                      }}>
                        <span style={{
                          fontSize: 14, textTransform: 'capitalize', color: '#475569'
                        }}>
                          {domain}
                        </span>
                        <span style={{ fontWeight: 600, color: '#7c3aed' }}>
                          {count}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default PartnershipDashboard;
